import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { resolveDefaultProgramRouteFromApi } from '@/modules/programs/services/resolveDefaultProgramRouteService';
import { useProgramRoleMenuPermissions } from '@/modules/programs/hooks/useProgramRoleMenuPermissions';
import { MENU_KEYS, type MenuKey } from '@/modules/programs/services/programRoleMenuPermissionService';
import { ErrorDisplay } from '@/modules/shared/components/ui/ErrorDisplay';
import { AuthGuardSkeleton } from '@/modules/shared/components/ui/AppSkeleton';
import { appRoutes, programRoutes } from '@/modules/shared/constants/routes';
import { resolveDashboardLandingPath } from '../dashboard-governance/DashboardLandingResolver';

const NON_SUPERADMIN_ALLOWED_MENU_KEYS = new Set<string>([
  MENU_KEYS.FINANCE_DASHBOARD,
  MENU_KEYS.WORKSPACE,
  MENU_KEYS.WORKSPACE_CASES,
  MENU_KEYS.OPERATIONAL_DASHBOARD,
  MENU_KEYS.PHARMA_WORKSPACE,
  MENU_KEYS.HOSPITAL_WORKSPACE,
  MENU_KEYS.HCP_WORKSPACE,
  MENU_KEYS.PATIENT_WORKSPACE,
  // Patient-related menus
  MENU_KEYS.PATIENTS,
  MENU_KEYS.PATIENT_360,
  MENU_KEYS.PATIENT_ACTIVITY,
  // Program configuration menus
  MENU_KEYS.PROGRAM_DASHBOARD,
  MENU_KEYS.ROLES_CONFIG,
  MENU_KEYS.USERS_CONFIG,
  MENU_KEYS.FORMS_CONFIG,
  MENU_KEYS.WORKFLOW_CONFIG,
  // Entity menus
  MENU_KEYS.HOSPITALS,
  MENU_KEYS.HCPS,
  // Case/Financial menus
  MENU_KEYS.ASSISTANCE_CASES,
  MENU_KEYS.ASSISTANCE_SETTINGS,
  MENU_KEYS.ASSISTANCE_CLAIMS,
  MENU_KEYS.FINANCIAL_PLAN,
  MENU_KEYS.CREATE_ASSISTANCE_CASE,
  // Communication menus
  MENU_KEYS.NOTIFICATIONS_CONFIG,
  MENU_KEYS.CALL_LOGS,
  MENU_KEYS.ADVERSE_EVENTS,
]);

interface ProgramModuleGuardProps {
  /** The menu key that must be accessible for this route. */
  menuKey: MenuKey | string;
  /** The content to render if access is granted. */
  children: ReactNode;
  /** Optional: redirect path when access is denied. Defaults to program dashboard. */
  redirectTo?: string;
  /** Optional: whether SuperAdmin can access this route. Defaults to true. */
  allowSuperAdmin?: boolean;
}

/**
 * ProgramModuleGuard
 *
 * Wraps a program-scoped route and blocks access if the current user's role
 * in this program does not have the required menu key permission.
 *
 * - SuperAdmin bypasses all checks.
 * - While permissions are loading, renders null (prevents flash of forbidden content).
 * - On access denial, redirects to the program dashboard (or custom redirectTo).
 *
 * Uses React Query — shares the same cache with Layout.tsx so no extra API calls are made.
 *
 * Usage (in App.tsx):
 * ```tsx
 * <Route path="hospitals" element={
 *   <ProgramModuleGuard menuKey="HOSPITALS">
 *     <HospitalListPage />
 *   </ProgramModuleGuard>
 * } />
 * ```
 */
export function ProgramModuleGuard({
  menuKey,
  children,
  redirectTo,
  allowSuperAdmin = true,
}: ProgramModuleGuardProps) {
  const { user } = useAuth();
  const params = useParams<{ programId?: string }>();
  const location = useLocation();
  const programId =
    params.programId ||
    location.pathname.match(/\/programs\/([a-f0-9-]{36})/i)?.[1];

  const [resolvedRedirectPath, setResolvedRedirectPath] = useState<string | null>(null);

  const { canAccessMenu, isLoading, error, refetch, dashboardKey, landingRoute, defaultLandingRoute } = useProgramRoleMenuPermissions({
    programId,
    enabled: !!programId && user?.role !== 'SuperAdmin' && user?.role !== 'ProductAdmin',
  });

  const canAccessRequestedModule = (user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin')
    ? true
    : NON_SUPERADMIN_ALLOWED_MENU_KEYS.has(menuKey);

  // ── Dashboard-mapping override ──────────────────────────────────
  // If the current path IS the user's configured landing route (set
  // via ProgramRoleDashboardMappings in the backend), allow access
  // regardless of the individual menu-key check.  The mapping is
  // admin-configured and already validated on the server side.
  const isUserConfiguredLandingRoute = (() => {
    if (!programId || !location.pathname) return false;
    const effectiveLanding = landingRoute || defaultLandingRoute || user?.landingRoute;
    if (!effectiveLanding) return false;
    const normCurrent = location.pathname.replace(/\/+$/, '').toLowerCase();
    // Normalize: if landing is relative, build full path
    const normLanding = effectiveLanding.startsWith('/')
      ? effectiveLanding.replace(/\/+$/, '').toLowerCase()
      : `/programs/${programId}/${effectiveLanding}`.replace(/\/+$/, '').toLowerCase();
    return normCurrent === normLanding || normCurrent.startsWith(normLanding + '/');
  })();

  // Resolve the default route when access is denied and no custom redirectTo is provided
  useEffect(() => {
    if (!isLoading && (!canAccessMenu(menuKey) || !canAccessRequestedModule) && !isUserConfiguredLandingRoute && !redirectTo && programId) {
      // Use the already-resolved landing route from the hook when available,
      // avoiding an extra network round-trip and the null-state race.
      const immediateRedirect = resolveDashboardLandingPath({
        programId,
        dashboardKey,
        landingRoute,
        defaultLandingRoute,
        fallbackRoute: programRoutes.workspace(programId),
      });
      if (immediateRedirect && immediateRedirect !== programRoutes.workspace(programId)) {
        setResolvedRedirectPath(immediateRedirect);
        return;
      }

      resolveDefaultProgramRouteFromApi(programId)
        .then((route) => {
          setResolvedRedirectPath(route);
        })
        .catch(() => {
          setResolvedRedirectPath(immediateRedirect);
        });
    }
  }, [isLoading, menuKey, redirectTo, programId, canAccessMenu, canAccessRequestedModule, isUserConfiguredLandingRoute, dashboardKey, landingRoute, defaultLandingRoute]);

  if (!programId) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-4">
        <ErrorDisplay message="Program context is missing from the route. Please navigate from the Programs menu and try again." />
      </div>
    );
  }

  // Optional SuperAdmin bypass control for routes explicitly restricted from SuperAdmin.
  if (user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin') {
    if (!allowSuperAdmin) {
      const destination = redirectTo ?? appRoutes.superadminDashboard();
      return <Navigate to={destination} replace />;
    }
    return <>{children}</>;
  }

  // While loading, render a visible fallback instead of a blank screen.
  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-4">
        <AuthGuardSkeleton message="Verifying program access..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 py-4">
        <ErrorDisplay
          message={error}
          onRetry={() => {
            void refetch();
          }}
          retryText="Retry Access Check"
        />
      </div>
    );
  }

  // Access allowed: either via menu-key permission OR via admin-configured
  // dashboard mapping (ProgramRoleDashboardMappings).
  if ((canAccessMenu(menuKey) && canAccessRequestedModule) || isUserConfiguredLandingRoute) {
    if (isUserConfiguredLandingRoute && !(canAccessMenu(menuKey) && canAccessRequestedModule)) {
      console.debug('[ProgramModuleGuard] Access granted via dashboard mapping override', {
        menuKey,
        landingRoute: landingRoute || user?.landingRoute,
        currentPath: location.pathname,
      });
    }
    return <>{children}</>;
  }

  // Access denied — redirect to provided path, resolved default route, or workspace
  const destination = redirectTo ?? resolvedRedirectPath ?? programRoutes.workspace(programId);

  // Prevent redirect loop: if the resolved destination matches the current path,
  // render children instead of infinitely redirecting to the same URL.
  if (location.pathname === destination || location.pathname.startsWith(destination + '/')) {
    return <>{children}</>;
  }

  return <Navigate to={destination} replace />;
}
